<script setup lang="ts">
import { ref, watch } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { STATUS_OPEN, STATUS_CLOSED } from '@/constants';
import { usePackageManagementStore } from '@/store/PackageManagementStore';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { formatDate } from '@/utils/dateUtils';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import { useComponentComposable } from '@/components/useComponentComposable';
import { useSeasonalComposable } from '@/components/useSeasonalComposable';
import { useViewComposable } from '@/views/composable';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import DataTable from 'primevue/datatable';
import Dialog from 'primevue/dialog';
import Select from 'primevue/select';
import ToggleSwitch from 'primevue/toggleswitch';
import InputText from 'primevue/inputtext';
import TableColumn from 'primevue/column';
import Tag from 'primevue/tag';
import ProgressSpinner from 'primevue/progressspinner';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import ManageInstaquoteRule from '@/views/internal/Package/ManageInstaquoteRule.vue';
import AdjustSeasonalityModal from '@/components/modal/AdjustSeasonalityModal.vue';
import ExportExcelFile from '@/components/modal/utils/ExportExcelFile.vue';
import ImportFileModal from '@/components/modal/ImportFileModal.vue';
import ProgressModal from '@/components/modal/ProgressModal.vue';
import ViewFlightRuleDataModal from '@/components/modal/ViewFlightRuleDataModal.vue';
import ViewVersionRevertModal from '@/components/modal/ViewVersionRevertModal.vue';
import SelectDatesDisplay from '@/components/seasonal/SelectDatesDisplay.vue';
import SeasonDurationDisplay from '@/components/seasonal/SeasonDurationDisplay.vue';

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const dialog = useDialog();
const packageManagementStore = usePackageManagementStore();
const currentLanguage = getCurrentLocale();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const instaquoteRuleList = ref([]);
const sheetsData = ref([]);
const { styleColumnExport, renderNameFileExport } = useViewComposable();
const { displayDate, checkValidity } = useComponentComposable();
const { initSeasonalValue } = useSeasonalComposable();
const widthList = [10, 40, 40, 10, 10, 12, 12, 10, 10, 15, 15, 12, 12, 12, 15, 10, 10, 10, 15, 15, 15, 15, 15, 15, 15, 15, 10];
const sheetColumnStyle = styleColumnExport(widthList);
const instaquoteRuleFilters = ref();
const loading = ref(false);
const isLoadingImport = ref(false);
const isShowModal = ref(false);
const isLoadingPage = ref(false);
const isSuccess = ref(false);
const isRevert = ref(false);
const statuses = ref([STATUS_OPEN, STATUS_CLOSED]);

const seasonal = ref({
  seasonalException: {
    ...initSeasonalValue()
  },
  seasonalSummer: {
    ...initSeasonalValue()
  },
  seasonalWinter: {
    ...initSeasonalValue()
  }
});

const getListInstaquoteRule = () => {
  packageManagementStore.getListInstaquoteRule().then((response) => {
    instaquoteRuleList.value = response.data.map((item) => {
      const depDateStart = new Date(item.depDateStart);
      const depDateEnd = new Date(item.depDateEnd);
      item.depDateStart = new Date(depDateStart.getTime() + depDateStart.getTimezoneOffset() * 60000);
      item.depDateEnd = new Date(depDateEnd.getTime() + depDateEnd.getTimezoneOffset() * 60000);
      return item;
    });
  });
};
getListInstaquoteRule();

const initInstaquoteRuleFilters = () => {
  instaquoteRuleFilters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    ruleType: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    gateCode: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    desCode: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    minDay: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    maxDay: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    ruleStatus: { operator: FilterOperator.OR, constraints: [{ value: STATUS_OPEN, matchMode: FilterMatchMode.STARTS_WITH }] }
  };
};
initInstaquoteRuleFilters();

const formatArray = (durations: string[], separate = ',') => {
  return durations ? durations.join(separate) : '';
};

const formatDataExport = (listData: any) => {
  return listData?.map((item: any) => {
    const data: any = {};
    data[t('management.package.type')] = item.ruleType;
    data[t('management.package.gateway')] = `${item.gateName || ''} (${item.gateCode})`;
    data[t('management.package.destination')] = `${item.desName || ''} (${item.desCode})`;
    data[t('management.package.min_days')] = item.minDay;
    data[t('management.package.max_days')] = item.maxDay;
    data[t('management.package.min_duration')] = item.minDuration;
    data[t('management.package.max_duration')] = item.maxDuration;
    data[t('management.package.min_pax')] = item.minPax;
    data[t('management.package.max_pax')] = item.maxPax;
    data[t('management.package.min_f_class')] = item.minFClass || 0;
    data[t('management.package.max_y_class')] = item.maxYClass || 0;
    data[t('management.package.summer_start')] = displayDate(item?.seasonalSummer?.startDay, item?.seasonalSummer?.startMonth, currentLanguage);
    data[t('management.package.summer_end')] = displayDate(item?.seasonalSummer?.endDay, item?.seasonalSummer?.endMonth, currentLanguage);
    data[t('management.package.summer_days')] = formatArray(item?.seasonalSummer?.dayOfWeeks, ',');
    data[t('management.package.summer_durations')] = formatArray(item?.seasonalSummer?.durations, ',');
    data[t('management.package.winter_start')] = displayDate(item?.seasonalWinter?.startDay, item?.seasonalWinter?.startMonth, currentLanguage);
    data[t('management.package.winter_end')] = displayDate(item?.seasonalWinter?.endDay, item?.seasonalWinter?.endMonth, currentLanguage);
    data[t('management.package.winter_days')] = formatArray(item?.seasonalWinter?.dayOfWeeks, ',');
    data[t('management.package.winter_durations')] = formatArray(item?.seasonalWinter?.durations, ',');
    data[t('management.package.exception_start')] = displayDate(item?.seasonalException?.startDay, item?.seasonalException?.startMonth, currentLanguage);
    data[t('management.package.exception_end')] = displayDate(item?.seasonalException?.endDay, item?.seasonalException?.endMonth, currentLanguage);
    data[t('management.package.exception_days')] = formatArray(item?.seasonalException?.dayOfWeeks, ',');
    data[t('management.package.exception_durations')] = formatArray(item?.seasonalException?.durations, ',');
    data[t('management.package.minimum_cost')] = item.minCost;
    data[t('management.package.apply_cost_rule')] = item.costRule;
    data[t('management.package.apply_rule_connector')] = item.connectorRule;
    data[t('management.package.estimated_ard_taxes')] = item.estimatedArdTax;
    data[t('common.status')] = item.ruleStatus;
    return data;
  });
};

const getSeverityStatus = (status: string) => {
  switch (status) {
    case STATUS_OPEN:
      return 'info';
    case STATUS_CLOSED:
      return 'danger';
  }
};

const updateRuleStatus = (sk: string, status: string) => {
  packageManagementStore
    .updateInstaquoteRuleStatus(sk, status)
    .then(() => {
      getListInstaquoteRule();
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};

const openDialog = (header: string, data?: any) => {
  const dialogRef = dialog.open(ManageInstaquoteRule, {
    data: {
      flightRule: data
    },
    props: {
      header: t(header),
      modal: true
    },
    emits: {
      closeModal: () => {
        toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
        dialogRef.close();
        getListInstaquoteRule();
      }
    }
  });
};

const editInstaquoteRule = (data: any) => {
  openDialog('management.package.update_instaquote_flight_rule', data);
};

const duplicateInstaquoteRule = (data: any) => {
  const payload = {
    ...data,
    sk: ''
  };
  openDialog('management.package.duplicate_instaquote_flight_rule', payload);
};

const addInstaquoteRule = () => {
  openDialog('management.package.add_instaquote_flight_rule');
};

const adjustSeasonalDates = () => {
  const dialogRef = dialog.open(AdjustSeasonalityModal, {
    props: {
      header: t('management.package.adjust_seasonal_dates'),
      modal: true
    },
    emits: {
      closeModal: () => {
        getListInstaquoteRule();
        dialogRef.close();
      }
    }
  });
};

const deleteFlightRule = (sk: string) => {
  packageManagementStore
    .deleteInstaquoteRule(sk)
    .then(() => {
      getListInstaquoteRule();
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};

const handleImportFile = async (file: any) => {
  isLoadingImport.value = true;
  isShowModal.value = true;
  isSuccess.value = true;
  isRevert.value = false;
  try {
    const payload = new FormData();
    payload.append('file', file);
    await packageManagementStore.importFileFlightRule(payload);
    getListInstaquoteRule();
  } catch (error) {
    isSuccess.value = false;
  } finally {
    isLoadingImport.value = false;
  }
};

const handleRevertFile = async (fileName: string) => {
  isRevert.value = true;
  isLoadingImport.value = true;
  isShowModal.value = true;
  isSuccess.value = true;
  try {
    await packageManagementStore.revertVersion(fileName);
    getListInstaquoteRule();
  } catch (error) {
    isSuccess.value = false;
    toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
  } finally {
    isLoadingImport.value = false;
  }
};

const handleApply = (data: any, file?: any, type?: string) => {
  const dialogRef = dialog.open(ViewFlightRuleDataModal, {
    props: {
      header: t('common.labels.review_upload_file'),
      style: {
        width: '95vw'
      },
      modal: true,
      maximizable: true
    },
    data: {
      flightRules: data,
      type,
      sheetData: formatDataExport(data),
      seasonal
    },
    emits: {
      onApply: async () => {
        dialogRef.close();
        if (type === 'IMPORT') {
          await handleImportFile(file);
        } else if (type === 'REVERT') {
          await handleRevertFile(file);
        }
      },
      onClose: () => {
        dialogRef.close();
        if (type === 'REVERT') {
          showRevertVersion('FLIGHT_RULE');
        }
      }
    }
  });
};

const showModalImportExcel = () => {
  const dialogRef = dialog.open(ImportFileModal, {
    props: {
      header: t('common.labels.upload_flight_rule'),
      modal: true
    },
    data: {
      url: packageManagementStore.getUrlUploadFileFlight
    },
    emits: {
      onApply: (data: any, file: any) => {
        handleApply(data, file, 'IMPORT');
        dialogRef.close();
      },
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const handleCloseModal = (value: boolean) => {
  if (isRevert.value === true) {
    showRevertVersion('FLIGHT_RULE');
  }
  isShowModal.value = value;
};

const showRevertVersion = async (type: string) => {
  try {
    const versions = await packageManagementStore.getVersionRevert(type);
    handleOpenModalRevert(versions?.data);
  } catch (error) {
    toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
  }
};

const handleGetPreviewVersion = async (fileName: string, type: string) => {
  try {
    isLoadingPage.value = true;
    const data = await packageManagementStore.getPreviewByFileName(fileName);
    handleApply(data?.data, fileName, type);
  } catch (error) {
    toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
  } finally {
    isLoadingPage.value = false;
  }
};

const handleOpenModalRevert = (versions: any) => {
  const dialogRef = dialog.open(ViewVersionRevertModal, {
    props: {
      header: t('common.buttons.revert'),
      style: {
        width: '95vw'
      },
      modal: true,
      maximizable: true
    },
    data: {
      versions
    },
    emits: {
      onPreview: (fileName: string) => {
        handleGetPreviewVersion(fileName, 'PREVIEW');
      },
      onRevert: (fileName: string) => {
        handleGetPreviewVersion(fileName, 'REVERT');
        dialogRef.close();
      },
      onExport: (fileName: string) => {
        handleGetPreviewVersion(fileName, 'EXPORT');
      },
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const handleApplyCostRule = (value: any, sk: string) => {
  packageManagementStore
    .updateCostRuleStatus(sk, value)
    .then(() => {
      getListInstaquoteRule();
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};

const handleApplyRuleConnector = (value: boolean, sk: string) => {
  packageManagementStore
    .updateRuleConnectorStatus(sk, value)
    .then(() => {
      getListInstaquoteRule();
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};

const getFlightRuleConfiguration = (config: any) => {
  return config?.flight_rule_configuration ?? {};
};

watch(instaquoteRuleList, (newVal) => {
  sheetsData.value = [
    {
      name: 'Sheet 1',
      data: formatDataExport(newVal)
    }
  ];
});
</script>

<template>
  <LoadingSpinner v-if="isLoadingPage" />
  <div class="card flight-rule-management">
    <div class="flex justify-content-end" v-if="getFlightRuleConfiguration(authorizations)?.add_flight_rule">
      <Button id="btn-adj-season" icon="pi pi-plus" :label="$t('management.package.adjust_seasonal')" class="mb-2 btn-adj mr-2" @click="adjustSeasonalDates()" />
      <Button id="btn-add-rule" icon="pi pi-plus" :label="$t('management.package.add_rule')" class="p-button-primary mb-2" @click="addInstaquoteRule()" />
    </div>
    <DataTable
      :value="instaquoteRuleList"
      v-model:filters="instaquoteRuleFilters"
      paginator
      showGridlines
      :loading="loading"
      :rows="10"
      dataKey="sk"
      filterDisplay="menu"
      :globalFilterFields="['ruleType', 'gateCode', 'desCode', 'minDay', 'maxDay', 'ruleStatus']"
    >
      <template #header>
        <div class="flex justify-content-between flex-col sm:flex-row">
          <div class="flex justify-content-left gap-3">
            <div>
              <Button id="btn-clear-filter" icon="pi pi-filter-slash" :label="$t('common.buttons.clear')" class="clear-fight-rule-btn mb-2" @click="initInstaquoteRuleFilters()" />
            </div>
            <div v-if="getFlightRuleConfiguration(authorizations)?.export_flight_rule">
              <ExportExcelFile id="btn-export-excel" class="export-fight-rule-btn" :sheets="sheetsData" :columnsStyle="sheetColumnStyle" :file-name="renderNameFileExport('Flight_Instaquote_Rule_Management')"></ExportExcelFile>
            </div>
            <div v-if="getFlightRuleConfiguration(authorizations)?.import_flight_rule">
              <Button id="btn-import-excel" :label="$t('common.buttons.import')" class="import-fight-rule-btn" @click="showModalImportExcel()" />
            </div>
            <div v-if="getFlightRuleConfiguration(authorizations)?.revert_flight_rule">
              <Button id="btn-revert-excel" :label="$t('common.buttons.revert')" class="revert-fight-rule-btn" @click="showRevertVersion('FLIGHT_RULE')" />
            </div>
          </div>
          <div class="justify-content-right">
            <span class="p-input-icon-left mb-2">
              <i class="pi pi-search" />
              <InputText id="search-by-keyword" v-model="instaquoteRuleFilters['global'].value" :placeholder="$t('my_quote.search_keyword')" class="w-full" />
            </span>
          </div>
        </div>
      </template>
      <template #empty> {{ $t('management.no_data_found') }} </template>
      <template #loading>
        <ProgressSpinner class="w-4rem h-4rem" strokeWidth="8" animationDuration="2s" />
      </template>
      <TableColumn field="ruleType" :header="$t('management.package.type')" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data, field }">
          <span class="long-text" v-tooltip="data[field]">
            {{ data[field] }}
          </span>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_type')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn field="gateCode" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <span class="white-space-nowrap">{{ $t('management.package.gateway') }} ({{ $t('management.package.code') }})</span>
        </template>
        <template #body="{ data }">
          <div class="flex flex-col">
            <span>{{ data.gateName }} ({{ data.gateCode }})</span>
          </div>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_gateway')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn field="desCode" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <span class="white-space-nowrap">{{ $t('management.package.destination') }} ({{ $t('management.package.code') }})</span>
        </template>
        <template #body="{ data }">
          <div class="flex flex-col">
            <span>{{ data.desName }} ({{ data.desCode }})</span>
          </div>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_destination')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn field="minDay" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col white-space-normal">
            <span>{{ $t('management.package.min_days') }}</span>
            <span>({{ $t('management.package.departure_quote') }})</span>
          </div>
        </template>
        <template #body="{ data }">
          <div class="flex flex-col">
            <span>{{ data.minDay }}</span>
          </div>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_min_days')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn field="maxDay" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col white-space-normal">
            <span>{{ $t('management.package.max_days') }}</span>
            <span>({{ $t('management.package.departure_quote') }})</span>
          </div>
        </template>
        <template #body="{ data }">
          <div class="flex flex-col">
            <span>{{ data.maxDay }}</span>
          </div>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_max_days')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn field="minDuration" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col white-space-normal">
            <span>{{ $t('management.package.min_duration') }}</span>
          </div>
        </template>
      </TableColumn>
      <TableColumn field="maxDuration" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col white-space-normal">
            <span>{{ $t('management.package.max_duration') }}</span>
          </div>
        </template>
      </TableColumn>
      <TableColumn class="white-space-nowrap" field="minPax" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col white-space-normal">
            <span>{{ $t('management.package.min_pax') }}</span>
          </div>
        </template>
      </TableColumn>
      <TableColumn class="white-space-nowrap" field="maxPax" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col white-space-normal">
            <span>{{ $t('management.package.max_pax') }}</span>
          </div>
        </template>
      </TableColumn>
      <TableColumn field="minFClass" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col">
            <span class="white-space-nowrap">{{ currentLanguage === 'fr' ? $t('management.package.class') : $t('management.package.min_f') }}</span>
            <span class="white-space-nowrap">{{ currentLanguage === 'fr' ? $t('management.package.min_f') : $t('management.package.class') }} ( % )</span>
          </div>
        </template>
        <template #body="{ data, field }">
          <span>{{ data[field] }} %</span>
        </template>
      </TableColumn>
      <TableColumn class="white-space-nowrap" field="maxYClass" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col">
            <span class="white-space-nowrap">{{ currentLanguage === 'fr' ? $t('management.package.class') : $t('management.package.max_y') }}</span>
            <span class="white-space-nowrap">{{ currentLanguage === 'fr' ? $t('management.package.max_y') : $t('management.package.class') }} ( % )</span>
          </div>
        </template>
        <template #body="{ data, field }">
          <span>{{ data[field] }} %</span>
        </template>
      </TableColumn>
      <TableColumn class="white-space-nowrap" field="seasonalSummer" :showAddButton="false" :showFilterOperator="false" dataType="date">
        <template #header>
          <div class="flex flex-col">
            <span class="white-space-nowrap">{{ $t('common.labels.summer_season_dates') }}</span>
          </div>
        </template>
        <template #body="{ data }">
          <div v-if="data.seasonalSummer">
            <p class="mb-0 font-bold">
              {{
                checkValidity(data?.seasonalSummer)
                  ? formatDate(new Date(new Date().getFullYear(), +data?.seasonalSummer?.startMonth - 1, +data?.seasonalSummer?.startDay), 'dd-LLL', true, currentLanguage) +
                    ' ' +
                    $t('common.labels.to') +
                    ' ' +
                    formatDate(new Date(new Date().getFullYear(), +data?.seasonalSummer?.endMonth - 1, +data?.seasonalSummer?.endDay), 'dd-LLL', true, currentLanguage)
                  : $t('common.labels.default_date_text')
              }}
            </p>
            <SelectDatesDisplay class="my-2" :checkAvailableDate="checkValidity(data?.seasonalSummer)" :listDayOfWeek="data?.seasonalSummer ? data?.seasonalSummer?.dayOfWeeks : seasonal.seasonalSummer?.dayOfWeeks" />
            <SeasonDurationDisplay :checkAvailableDate="checkValidity(data?.seasonalSummer)" :durations="data.seasonalSummer ? data.seasonalSummer?.durations : seasonal.seasonalSummer?.durations" />
          </div>
        </template>
      </TableColumn>
      <TableColumn class="white-space-nowrap" field="seasonalWinter" :showAddButton="false" :showFilterOperator="false" dataType="date">
        <template #header>
          <div class="flex flex-col">
            <span class="white-space-nowrap">{{ $t('common.labels.winter_season_dates') }}</span>
          </div>
        </template>
        <template #body="{ data }">
          <div v-if="data.seasonalWinter">
            <p class="mb-0 font-bold">
              {{
                checkValidity(data?.seasonalWinter)
                  ? formatDate(new Date(new Date().getFullYear(), +data?.seasonalWinter?.startMonth - 1, +data?.seasonalWinter?.startDay), 'dd-LLL', true, currentLanguage) +
                    ' ' +
                    $t('common.labels.to') +
                    ' ' +
                    formatDate(new Date(new Date().getFullYear(), +data?.seasonalWinter?.endMonth - 1, +data?.seasonalWinter?.endDay), 'dd-LLL', true, currentLanguage)
                  : $t('common.labels.default_date_text')
              }}
            </p>
            <SelectDatesDisplay class="my-2" :checkAvailableDate="checkValidity(data?.seasonalWinter)" :listDayOfWeek="data?.seasonalWinter ? data?.seasonalWinter?.dayOfWeeks : seasonal.seasonalWinter?.dayOfWeeks" />
            <SeasonDurationDisplay :checkAvailableDate="checkValidity(data?.seasonalWinter)" :durations="data.seasonalWinter ? data.seasonalWinter?.durations : seasonal.seasonalWinter?.durations" />
          </div>
        </template>
      </TableColumn>
      <TableColumn class="white-space-nowrap" field="seasonalException" :showAddButton="false" :showFilterOperator="false" dataType="date">
        <template #header>
          <div class="flex flex-col">
            <span class="white-space-nowrap">{{ $t('common.labels.exception_season_dates') }}</span>
          </div>
        </template>
        <template #body="{ data }">
          <div v-if="data.seasonalException">
            <p class="mb-0 font-bold">
              {{
                checkValidity(data?.seasonalException)
                  ? formatDate(new Date(new Date().getFullYear(), +data?.seasonalException?.startMonth - 1, +data?.seasonalException?.startDay), 'dd-LLL', true, currentLanguage) +
                    ' ' +
                    $t('common.labels.to') +
                    ' ' +
                    formatDate(new Date(new Date().getFullYear(), +data?.seasonalException?.endMonth - 1, +data?.seasonalException?.endDay), 'dd-LLL', true, currentLanguage)
                  : ''
              }}
            </p>
            <SelectDatesDisplay :checkAvailableDate="checkValidity(data?.seasonalException)" class="my-2" :listDayOfWeek="data?.seasonalException ? data?.seasonalException?.dayOfWeeks : seasonal.seasonalException?.dayOfWeeks" />
            <SeasonDurationDisplay :checkAvailableDate="checkValidity(data?.seasonalException)" :durations="data.seasonalException ? data.seasonalException?.durations : seasonal.seasonalException?.durations" />
          </div>
        </template>
      </TableColumn>
      <TableColumn class="white-space-nowrap" field="minCost" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col">
            <span class="white-space-nowrap">{{ currentLanguage === 'fr' ? $t('management.package.cost') : $t('management.package.minimum') }}</span>
            <span class="white-space-nowrap">{{ currentLanguage === 'fr' ? $t('management.package.minimum') : $t('management.package.cost') }}</span>
          </div>
        </template>
        <template #body="{ data }">
          {{ data.minCost ?? 0 }}
        </template>
      </TableColumn>
      <TableColumn class="cost-rule" field="costRule" :header="$t('management.package.apply_cost_rule')" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data }">
          <div class="flex justify-content-center">
            <Checkbox :disabled="!getFlightRuleConfiguration(authorizations)?.edit_flight_rule" v-model="data.costRule" :binary="true" @update:modelValue="handleApplyCostRule($event, data.sk)" />
          </div>
        </template>
      </TableColumn>
      <TableColumn class="rule-connector" field="connectorRule" :header="$t('management.package.apply_rule_connector')" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data }">
          <div class="flex justify-content-center">
            <Checkbox :disabled="!getFlightRuleConfiguration(authorizations)?.edit_flight_rule" v-model="data.connectorRule" :binary="true" @update:modelValue="handleApplyRuleConnector($event, data.sk)" />
          </div>
        </template>
      </TableColumn>
      <TableColumn class="white-space-nowrap" field="estimatedArdTax" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col">
            <span class="white-space-nowrap">{{ $t('management.package.estimated_ard_taxes') }}</span>
          </div>
        </template>
        <template #body="{ data }">
          {{ data.estimatedArdTax ?? 0 }}
        </template>
      </TableColumn>
      <TableColumn field="ruleStatus" :header="$t('common.status')" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data }">
          <ToggleSwitch :disabled="!getFlightRuleConfiguration(authorizations)?.status_flight_rule" v-model="data.ruleStatus" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" @change="updateRuleStatus(data.sk, data.ruleStatus)" />
        </template>
        <template #filter="{ filterModel }">
          <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
            <template #option="slotProps">
              <Tag :value="slotProps.option" :severity="getSeverityStatus(slotProps.option)" />
            </template>
          </Select>
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn :header="$t('common.action')">
        <template #body="{ data }">
          <div class="flex">
            <Button v-if="getFlightRuleConfiguration(authorizations)?.edit_flight_rule" icon="pi pi-pencil" class="p-button-text" @click="editInstaquoteRule(data)" />
            <Button icon="pi pi-copy" class="p-button-text" @click="duplicateInstaquoteRule(data)" />
            <Button v-if="getFlightRuleConfiguration(authorizations)?.delete_flight_rule" icon="pi pi-trash" class="p-button-text" @click="deleteFlightRule(data.sk)" />
          </div>
        </template>
      </TableColumn>
    </DataTable>
    <Dialog v-model:visible="isShowModal" :header="$t('common.labels.uploading_flight_rule')" modal :closable="!isLoadingImport">
      <ProgressModal :isProgress="isLoadingImport" @handleClose="handleCloseModal" :isSuccess="isSuccess" :isRevert="isRevert" />
    </Dialog>
  </div>
</template>
